import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, User, Eye, EyeOff, Loader2, GraduationCap } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_beauty-wellness-spot/artifacts/y6uqcyxn_1683577696901.png";

const translations = {
  en: {
    title: 'Training Access',
    subtitle: 'Login to access your courses and certifications',
    login: 'Login',
    register: 'Register',
    email: 'Email',
    password: 'Password',
    name: 'Full Name',
    loginBtn: 'Sign In',
    registerBtn: 'Create Account',
    orContinueWith: 'Or continue with',
    googleLogin: 'Continue with Google',
    noAccount: "Don't have an account?",
    haveAccount: 'Already have an account?',
    forgotPassword: 'Forgot password?',
    error: 'An error occurred',
    backToSite: 'Back to site'
  },
  fr: {
    title: 'Accès Formation',
    subtitle: 'Connectez-vous pour accéder à vos cours et certifications',
    login: 'Connexion',
    register: 'Inscription',
    email: 'Email',
    password: 'Mot de passe',
    name: 'Nom complet',
    loginBtn: 'Se connecter',
    registerBtn: 'Créer un compte',
    orContinueWith: 'Ou continuer avec',
    googleLogin: 'Continuer avec Google',
    noAccount: "Pas encore de compte ?",
    haveAccount: 'Déjà un compte ?',
    forgotPassword: 'Mot de passe oublié ?',
    error: 'Une erreur est survenue',
    backToSite: 'Retour au site'
  },
  pt: {
    title: 'Acesso à Formação',
    subtitle: 'Inicie sessão para aceder aos seus cursos e certificações',
    login: 'Entrar',
    register: 'Registar',
    email: 'Email',
    password: 'Palavra-passe',
    name: 'Nome completo',
    loginBtn: 'Entrar',
    registerBtn: 'Criar conta',
    orContinueWith: 'Ou continuar com',
    googleLogin: 'Continuar com Google',
    noAccount: 'Não tem conta?',
    haveAccount: 'Já tem conta?',
    forgotPassword: 'Esqueceu a palavra-passe?',
    error: 'Ocorreu um erro',
    backToSite: 'Voltar ao site'
  },
  de: {
    title: 'Schulungszugang',
    subtitle: 'Melden Sie sich an, um auf Ihre Kurse und Zertifikate zuzugreifen',
    login: 'Anmelden',
    register: 'Registrieren',
    email: 'E-Mail',
    password: 'Passwort',
    name: 'Vollständiger Name',
    loginBtn: 'Anmelden',
    registerBtn: 'Konto erstellen',
    orContinueWith: 'Oder fortfahren mit',
    googleLogin: 'Mit Google fortfahren',
    noAccount: 'Kein Konto?',
    haveAccount: 'Bereits ein Konto?',
    forgotPassword: 'Passwort vergessen?',
    error: 'Ein Fehler ist aufgetreten',
    backToSite: 'Zurück zur Seite'
  },
  es: {
    title: 'Acceso a Formación',
    subtitle: 'Inicie sesión para acceder a sus cursos y certificaciones',
    login: 'Iniciar sesión',
    register: 'Registrarse',
    email: 'Correo electrónico',
    password: 'Contraseña',
    name: 'Nombre completo',
    loginBtn: 'Iniciar sesión',
    registerBtn: 'Crear cuenta',
    orContinueWith: 'O continuar con',
    googleLogin: 'Continuar con Google',
    noAccount: '¿No tiene cuenta?',
    haveAccount: '¿Ya tiene cuenta?',
    forgotPassword: '¿Olvidó su contraseña?',
    error: 'Se produjo un error',
    backToSite: 'Volver al sitio'
  }
};

const LoginPage = () => {
  const { language } = useLanguage();
  const { login, register, loginWithGoogle, error: authError } = useAuth();
  const navigate = useNavigate();
  
  const t = translations[language] || translations.en;
  
  const [mode, setMode] = useState('login'); // login or register
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (mode === 'login') {
        await login(formData.email, formData.password);
      } else {
        await register(formData.email, formData.password, formData.name);
      }
      navigate('/my-courses');
    } catch (err) {
      setError(err.response?.data?.detail || t.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-kamit-cream to-kamit-beige flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-kamit-dark relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-kamit-brown/90 to-kamit-dark"></div>
        <div className="relative z-10 flex flex-col items-center justify-center w-full p-12 text-center">
          <img src={LOGO_URL} alt="IPRBC KAMIT" className="h-32 mb-8" />
          <h1 className="font-heading text-4xl text-kamit-beige mb-4">IPRBC KAMIT</h1>
          <p className="text-kamit-gold text-lg italic mb-8">
            Instituts Professionnels de Relaxations et de Beautés Corporelles
          </p>
          <div className="flex items-center gap-3 bg-kamit-gold/20 rounded-xl p-6">
            <GraduationCap className="h-12 w-12 text-kamit-gold" />
            <div className="text-left">
              <p className="text-kamit-beige font-medium">Formation Professionnelle</p>
              <p className="text-kamit-beige/70 text-sm">Accédez à nos 18 formations certifiantes</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img src={LOGO_URL} alt="IPRBC KAMIT" className="h-20 mx-auto mb-4" />
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl text-kamit-brown mb-2">{t.title}</h2>
              <p className="text-kamit-brown/60">{t.subtitle}</p>
            </div>

            {/* Mode Toggle */}
            <div className="flex bg-kamit-cream rounded-xl p-1 mb-6">
              <button
                onClick={() => setMode('login')}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                  mode === 'login' 
                    ? 'bg-kamit-gold text-kamit-dark' 
                    : 'text-kamit-brown hover:bg-kamit-beige'
                }`}
              >
                {t.login}
              </button>
              <button
                onClick={() => setMode('register')}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                  mode === 'register' 
                    ? 'bg-kamit-gold text-kamit-dark' 
                    : 'text-kamit-brown hover:bg-kamit-beige'
                }`}
              >
                {t.register}
              </button>
            </div>

            {/* Error Message */}
            {(error || authError) && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">
                {error || authError}
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'register' && (
                <div>
                  <Label htmlFor="name" className="text-kamit-brown">{t.name}</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-kamit-brown/40" />
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="pl-10 border-kamit-brown/20 focus:border-kamit-gold"
                      required={mode === 'register'}
                      data-testid="register-name-input"
                    />
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="email" className="text-kamit-brown">{t.email}</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-kamit-brown/40" />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="pl-10 border-kamit-brown/20 focus:border-kamit-gold"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-kamit-brown">{t.password}</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-kamit-brown/40" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="pl-10 pr-10 border-kamit-brown/20 focus:border-kamit-gold"
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-kamit-brown/40 hover:text-kamit-brown"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-kamit-gold hover:bg-kamit-gold/90 text-kamit-dark rounded-full py-6"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  mode === 'login' ? t.loginBtn : t.registerBtn
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-kamit-brown/10"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-kamit-brown/50">{t.orContinueWith}</span>
              </div>
            </div>

            {/* Google Login */}
            <Button
              type="button"
              variant="outline"
              onClick={loginWithGoogle}
              className="w-full border-kamit-brown/20 text-kamit-brown hover:bg-kamit-cream rounded-full py-6"
              data-testid="google-login-btn"
            >
              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              {t.googleLogin}
            </Button>

            {/* Back to site */}
            <div className="text-center mt-6">
              <Link to="/" className="text-kamit-brown/60 hover:text-kamit-brown text-sm">
                ← {t.backToSite}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
